import React from 'react';
import { observer } from 'mobx-react';
import { observable, action, makeObservable, } from 'mobx';
import Checkbox from 'rc-checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard } from '@fortawesome/free-solid-svg-icons'

import { type NoteInfo } from '@root/models/NoteInfo';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { apiService } from '@services/ApiService';
import { type IModalDialogContent, type ModalButtonOptions, ModalButtonType, type ModalDialogOptions, ModalWindow } from '@components/Modal';

import 'rc-checkbox/assets/index.css';

export type UpdateSharingOptionsArgs = {
  shareId?: string;
  isReadOnly: boolean;
};

export type ShareNoteDialogProps = {
  note: NoteInfo;
  onUpdate: (args: UpdateSharingOptionsArgs) => void;
};

type SharingData = {
  id: string;
  name: string;
  readOnly?: boolean;
  shareId: string | null;
};

@observer
export default class ShareNoteDialog extends React.Component<ShareNoteDialogProps> implements IModalDialogContent<string> {
  @observable private _showCopiedHint = false;
  @observable private _isReadOnly = true;
  @observable private _shareId: string | null = null;
  @observable private _data: SharingData | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _inputRef: React.RefObject<HTMLInputElement> = React.createRef();
  private _copyRef: React.RefObject<HTMLButtonElement> = React.createRef();
  private _copyTimer: number | null = null;

  constructor(props: ShareNoteDialogProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(){
    const { note } = this.props;

    const data = await apiService.get<SharingData>(`/api/note/${note.id}/share`, null, {
      loader: this._loader
    });

    this._data = data;
    this._shareId = data.shareId;
    this._isReadOnly = data.readOnly ?? true;
  }

  public getModalOptions(window: ModalWindow<string>): ModalDialogOptions<string> {
    const buttons: ModalButtonOptions<string>[] = [];
    
    buttons.push({
      type: ModalButtonType.Cancel,
      onClick: () => {
        window.close();
      }
    });

    if (this._shareId){
      buttons.push({
        title: 'Stop sharing',
        type: ModalButtonType.Remove,
        onClick: async () => {
          await this._onStopSharingClick();
        }
      });
    }

    if (!this._shareId && this._data){
      buttons.push({
        title: `Start sharing (${(this._isReadOnly ? 'read-only' : 'full control')})`,
        type: ModalButtonType.Save,
        onClick: async () => {
          await this._onSaveClick();
        }
      });
    }

    if (this._shareId && this._data && this._data.readOnly !== this._isReadOnly){
      buttons.push({
        title: `Update sharing as ${(this._isReadOnly ? 'read-only' : 'full control')}`,
        type: ModalButtonType.Save,
        onClick: async () => {
          await this._onSaveClick();
        }
      });
    }

    return {
      title: `Share settings`,
      width: 600,
      buttons: buttons,
      loader: this._loader
    };
  }

  render(){
    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {};

    if (this._shareId){
      inputProps.readOnly = true;
      inputProps.value = window.location.origin + "/s/" + this._shareId;
    } else {
      inputProps.disabled = true;
      inputProps.value = 'Note is not shared (private)';
    }

    return (
      <>
        <div className="form-group">
          <div className="input-group">
            <div className="input-group-prepend" style={{position: 'relative'}}>
              <button disabled={!this._shareId} ref={this._copyRef} className="btn btn-outline-secondary" onClick={this._onCopyClicked}>
                <FontAwesomeIcon icon={faClipboard} className='fa-icon'/>
              </button>
              {this._showCopiedHint && (
                <div className="copy-tooltip">
                  Copied!
                </div>
              )}
            </div>
            <input className="form-control" name="url" ref={this._inputRef} {...inputProps} onClick={this._onInputClick} onFocus={this._onInputFocus} onMouseUp={this._onMouseUp} type="text" />
          </div>
        </div>
        <div className="form-group">
          <div className="col-md-12">
            <label className="checkbox-label">
              <Checkbox checked={this._isReadOnly} onChange={e => this._isReadOnly = e.target.checked}/>
              &nbsp; Share as read-only
            </label>
          </div>
        </div>
        <div className="clearfix"></div>
      </>
    );
  }

  @action.bound
  private _onInputClick(){
    this._inputRef.current?.select();
  }

  private _copyToClipboard(text: string) {
    const input = document.createElement('input');
    input.style.position = 'fixed';
    input.style.top = '-5px';
    input.style.border = 'none';
    input.style.outline = 'none';
    input.style.boxShadow = 'none';
    input.style.background = 'transparent';
    input.value = text;
    document.body.appendChild(input);
    input.focus();
    input.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(input);
    }
  }
  
  @action.bound
  private _onInputFocus(){
    this._inputRef?.current?.focus();
  }

  @action.bound
  private _onMouseUp(e: React.MouseEvent){
    e.preventDefault();
    return false;
  }

  @action.bound
  private _onCopyClicked(){
    this._copyToClipboard(window.location.origin + "/s/" + this._shareId);
    this._showCopiedHint = true;
    if (this._copyTimer){
      window.clearTimeout(this._copyTimer);
      this._copyTimer = null;
    }

    this._copyTimer = window.setTimeout(() => {
      this._copyTimer = null;
      this._showCopiedHint = false;
    }, 3000);
  }

  @action.bound
  private async _onSaveClick(){
    const { note, onUpdate } = this.props;

    const shareSettings = {
      readOnly: this._isReadOnly
    };
    
    const data = await apiService.post<{shareId: string}>(`/api/note/${note.id}/share`, shareSettings, {
      loader: this._loader
    });

    if (this._data){
      this._data.shareId = data.shareId;
      this._data.readOnly = this._isReadOnly;
    }
    this._shareId = data.shareId;
    onUpdate({shareId: data.shareId, isReadOnly: this._isReadOnly});
  }

  @action.bound
  private async _onStopSharingClick(){
    const { note, onUpdate } = this.props;

    await apiService.delete(`/api/note/${note.id}/share`, {
      loader: this._loader
    });

    this._shareId = null;
    if (this._data){
      this._data.shareId = null;
    }

    onUpdate({isReadOnly: this._isReadOnly});
  }
}